/**
 * One sales-order row in the Add/Edit Direct Delivery grid, with its
 * expandable product sub-rows.
 *
 * Split out of DirectDeliveryModal (CR-7717) to keep that component under the
 * 500-line limit; the markup is unchanged.
 *
 * @link DirectDeliveryModal for the consumer.
 */
import { Fragment } from 'react';
import type { DirectDeliveryOrder } from '../../../lib/api';
import { Checkbox } from '../../ui/checkbox';
import PartNumberLink from '../shared/PartNumberLink';
import { MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import type { LocalColumn } from './DirectDeliveryColumns';

/** Get a cell value from an order based on column id */
function getOrderCellValue(order: DirectDeliveryOrder, colId: string): string {
  switch (colId) {
    case 'delivery_date':
      return order.delivery_date
        ? new Date(order.delivery_date + 'T00:00:00').toLocaleDateString()
        : '-';
    case 'item_code': {
      if (order.products.length === 1) return order.products[0].code;
      return order.products.map(p => p.code).join(', ');
    }
    case 'description': {
      if (order.products.length === 1) return order.products[0].desc;
      return order.products[0].desc + (order.products.length > 1 ? ` (+${order.products.length - 1})` : '');
    }
    case 'ship_to_address': return order.address || '-';
    case 'postcode': return order.postcode || '-';
    case 'bp_reference': return order.bp_reference || '-';
    case 'creation_date':
      return order.creation_date
        ? new Date(order.creation_date + 'T00:00:00').toLocaleDateString()
        : '-';
    case 'date_updated':
      return order.date_updated
        ? new Date(order.date_updated + 'T00:00:00').toLocaleDateString()
        : '-';
    case 'drop_number': return order.drop_number || '-';
    case 'so_number': return order.id;
    case 'customer': return order.customer || '-';
    case 'fill_percent': return `${order.fill_percent}%`;
    default: return '-';
  }
}

interface OrderRowProps {
  order: DirectDeliveryOrder;
  visibleCols: LocalColumn[];
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  routingOrigin: string;
}

export default function OrderRow({ order, visibleCols, isSelected, isExpanded, onToggleSelect, onToggleExpand, routingOrigin }: OrderRowProps) {
  return (
    <>
      <tr className={`hover:bg-muted/50 ${isSelected ? 'bg-blue-50' : ''}`}>
        <td className="py-2 px-2">
          <button onClick={onToggleExpand} className="text-muted-foreground hover:text-foreground">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="py-2 px-2">
          <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} />
        </td>
        {visibleCols.map(col => (
          <td key={col.id} className={`py-2 px-2 ${col.id === 'fill_percent' ? 'text-right' : ''} ${col.id === 'ship_to_address' || col.id === 'description' ? 'text-xs truncate max-w-[200px]' : ''} ${col.id === 'item_code' || col.id === 'drop_number' || col.id === 'so_number' ? 'font-mono text-xs' : ''}`}>
            {col.id === 'postcode' && order.postcode ? (
              routingOrigin ? (
                <a
                  href={`https://www.google.com/maps/dir/${encodeURIComponent(routingOrigin)}/${encodeURIComponent(order.postcode)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                  title={`Directions from ${routingOrigin} to ${order.postcode}`}
                >
                  <MapPin className="h-3 w-3" />
                  {order.postcode}
                </a>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  {order.postcode}
                </span>
              )
            ) : col.id === 'item_code' ? (
              // CR-9120: each item code opens its PO lines in a new tab.
              order.products.map((p, i) => (
                <Fragment key={`${p.code}-${i}`}>
                  {i > 0 && ', '}
                  <PartNumberLink partNumber={p.code} />
                </Fragment>
              ))
            ) : col.id === 'fill_percent' ? (
              <span className={`font-medium ${order.fill_percent > 90 ? 'text-red-600' : order.fill_percent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                {order.fill_percent}%
              </span>
            ) : (
              getOrderCellValue(order, col.id)
            )}
          </td>
        ))}
      </tr>
      {isExpanded && order.products.map((product, idx) => (
        <tr key={`${order.id}-${idx}`} className="bg-muted/20">
          <td className="py-1 px-2" />
          <td className="py-1 px-2" />
          <td className="py-1 px-2" colSpan={Math.ceil(visibleCols.length / 2)}>
            <span className="text-xs font-mono"><PartNumberLink partNumber={product.code} /></span>
            <span className="text-xs text-muted-foreground ml-3">{product.desc}</span>
          </td>
          <td className="py-1 px-2 text-right text-xs" colSpan={Math.floor(visibleCols.length / 2)}>
            Qty: {product.qty} &middot; Load Qty: {product.load_qty}
          </td>
        </tr>
      ))}
    </>
  );
}
